import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput, SelectInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  GD_LOHNART_SICHTBARKEIT_LABELS,
  getGdLohnarten,
  normalizeGdLohnartSichtbarkeit,
  saveGdLohnarten,
  type GdLohnart,
  type GdLohnartSichtbarkeit,
} from "@/lib/dokumente";
import { toast } from "@/lib/toast";
import { SettingsAccessDenied } from "../../SettingsLayout";
import { useSettingsAcl } from "../../lib/useSettingsAcl";

interface EditableLohnart extends GdLohnart {
  key: string;
}

function toEditable(items: GdLohnart[]): EditableLohnart[] {
  return items.map((item, index) => ({
    ...item,
    sichtbarkeit: normalizeGdLohnartSichtbarkeit(item.sichtbarkeit),
    key: item.id || `row-${index}`,
  }));
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function GdLohnartenSection() {
  const acl = useSettingsAcl();
  const [rows, setRows] = useState<EditableLohnart[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await getGdLohnarten();
      setRows(toEditable(items));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Lohnarten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!acl.loading && !acl.canAuftraege) {
    return <SettingsAccessDenied />;
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload: GdLohnart[] = rows.map((row, index) => ({
        id: row.id.trim() || slugify(row.bezeichnung) || `lohn-${index + 1}`,
        bezeichnung: row.bezeichnung.trim(),
        stundensatz_netto: Number(row.stundensatz_netto) || 0,
        sort_index: index,
        sichtbarkeit: normalizeGdLohnartSichtbarkeit(row.sichtbarkeit),
      }));
      const saved = await saveGdLohnarten(payload);
      setRows(toEditable(saved));
      toast.success("Lohnarten gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Lohnarten"
      description="Diese Lohnarten stehen in der Leistungskalkulation von Geschäftsdokumenten zur Verfügung. Lohnarten mit Sichtbarkeit „Nur Büro“ sind nur für Mitglieder der Gruppe Büro sichtbar."
      actions={
        <Button type="button" onClick={() => void handleSave()} disabled={saving || loading}>
          Speichern
        </Button>
      }
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
          <Spinner />
          Lohnarten werden geladen …
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              key={row.key}
              className="grid gap-3 rounded-lg border border-separator bg-card-elevated p-3 sm:grid-cols-[1fr_160px_140px_auto]"
            >
              <TextInput
                label="Bezeichnung"
                value={row.bezeichnung}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, bezeichnung: e.target.value } : item,
                    ),
                  )
                }
              />
              <TextInput
                label="Standard-Stundensatz netto"
                type="number"
                value={String(row.stundensatz_netto)}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((item, i) =>
                      i === index
                        ? { ...item, stundensatz_netto: Number(e.target.value) }
                        : item,
                    ),
                  )
                }
              />
              <SelectInput
                label="Sichtbarkeit"
                value={row.sichtbarkeit || "alle"}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((item, i) =>
                      i === index
                        ? { ...item, sichtbarkeit: e.target.value as GdLohnartSichtbarkeit }
                        : item,
                    ),
                  )
                }
              >
                {(Object.entries(GD_LOHNART_SICHTBARKEIT_LABELS) as [GdLohnartSichtbarkeit, string][]).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ),
                )}
              </SelectInput>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                >
                  Entfernen
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              setRows((prev) => [
                ...prev,
                {
                  key: `new-${Date.now()}`,
                  id: "",
                  bezeichnung: "",
                  stundensatz_netto: 0,
                  sort_index: prev.length,
                  sichtbarkeit: "alle",
                },
              ])
            }
          >
            Lohnart hinzufügen
          </Button>
        </div>
      )}
    </Card>
  );
}
